<template>
  <div>
    <header class="pb-16 bg-gray-100 px-24">
      <div class="flex">
        <div
          class="bg-blue-500 flex flex-col items-center justify-end px-4 pt-16 pb-2 min-width-32"
        >
          <h1 class="text-2xl font-bold text-white">OFFER</h1>
        </div>
        <div class="flex-1 flex flex-col items-end justify-end pb-2">
          <h1 class="text-2xl text-gray-800">
            <span class="text-blue-500 font-bold"> #{{ offer.number || '00000' }}</span> -
            {{ issueDate }}
          </h1>
        </div>
      </div>

      <div class="flex mt-16 space-x-8">
        <div class="flex-1">
          <h5 class="text-xxs font-bold tracking-widest text-blue-500 text-left mb-2">FROM</h5>
          <div>
            <div class="text-sm font-bold mb-4">
              {{ offer.issuerName || '-' }}
            </div>
            <div class="text-xs">
              {{ offer.issuerBusiness || '-' }}
            </div>
            <div class="text-xs">
              {{ offer.issuerAddress || '-' }}
            </div>
            <div class="text-xs">
              {{ offer.issuerVatNumber || '-' }}
            </div>
            <div class="text-xs">
              {{ offer.issuerVatOffice || '-' }}
            </div>
          </div>
        </div>
        <div class="flex-1">
          <h5 class="text-xxs font-bold tracking-widest text-blue-500 text-right mb-2">TO</h5>
          <div>
            <div class="text-right text-sm font-bold mb-4">
              {{ offer.recipientName || '-' }}
            </div>
            <div class="text-right text-xs">
              {{ offer.recipientBusiness || '-' }}
            </div>
            <div class="text-right text-xs">
              {{ offer.recipientAddress || '-' }}
            </div>
            <div class="text-right text-xs">
              {{ offer.recipientVatNumber || '-' }}
            </div>
            <div class="text-right text-xs">
              {{ offer.recipientVatOffice || '-' }}
            </div>
          </div>
        </div>
      </div>
    </header>

    <main>
      <div class="px-20 py-16">
        <table class="table-auto w-full border-collapse">
          <thead>
            <tr class="border-b-2 border-blue-500 text-blue-500 text-xxs uppercase tracking-wider">
              <th class="text-center pb-2 px-4 w-px whitespace-nowrap">#</th>
              <th class="text-left pb-2">Description</th>
              <th class="w-32 text-right pb-2 pr-4">Price</th>
            </tr>
          </thead>

          <OfferLine
            v-for="(offerLine, idx) in rootOfferLines"
            :key="idx"
            :line-number="idx + 1"
            :offer-line="offerLine"
            :offer-lines="childOfferLines"
          />
          <tfoot>
            <tr class="text-sm">
              <td class="border-t-2 border-blue-500 pt-2" />
              <td class="border-t-2 border-blue-500 text-right pt-2">Sub total</td>
              <td class="border-t-2 border-blue-500 text-right pt-2 px-4">
                {{ totalNetAmountHumanReadable }} &euro;
              </td>
            </tr>
            <tr class="text-sm">
              <td />
              <td class="text-right"><template v-if="vatRate === 0"> * </template>VAT (0%)</td>
              <td class="text-right px-4">{{ totalVatAmountHumanReadable }} &euro;</td>
            </tr>
            <tr class="text-sm font-bold">
              <td />
              <td class="text-right">Total</td>
              <td class="text-right px-4">{{ totalHumanReadable }} &euro;</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </main>

    <footer class="px-24">
      <table class="auto w-full">
        <tbody>
          <tr>
            <td class="align-top">
              <div class="flex-1 text-xxs">
                <h3 class="text-base font-bold text-blue-600">
                  Thank you for using {{ offer.issuerName }}.
                </h3>
              </div>
            </td>
            <td class="align-top">
              <div class="flex-1 text-xxs">
                <h5
                  class="text-xxs uppercase font-bold tracking-widest text-blue-500 text-right mb-2"
                >
                  ΑΠΟΔΟΧΗ ΠΡΟΣΦΟΡΑΣ
                </h5>
                <div class="pt-16 text-right text-xxs">
                  <span>Σφραγίδα - Υπογραφή</span>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="text-center text-xxs mt-16">
        <template v-if="paymentAccountNumbers.length > 0">
          <div class="uppercase font-bold tracking-widest text-blue-500 mb-1">
            ΤΡΑΠΕΖΙΚΟΙ ΛΟΓΑΡΙΑΣΜΟΙ
          </div>
          <span class="uppercase tracking-widest">{{ concatenatedPaymentAccountNumbers }}</span>
        </template>
        <!-- &bull;
        <span class="uppercase tracking-widest">EUROBANK: GR3702602510000550201402049</span> -->
        <p v-if="vatRate === 0" class="mt-2">
          * VAT zero-rated: Customer to account for any VAT arising on this supply in accordance
          with Article 196, Council Directive 2006/112/EC
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import moment from 'moment';
import Numbro from '@/utils/numbro';
import OfferLine from './printableOffer/OfferLine.vue';

const offerLineNetAmount = (offerLine) => offerLine.quantity * offerLine.pricePerUnit;

const offerLineVatAmount = (offerLine) => offerLine.vatRate * offerLineNetAmount(offerLine);

export default {
  components: {
    OfferLine,
  },
  props: {
    offer: {
      type: Object,
      required: true,
    },
    accountRelation: {
      type: Object,
      required: true,
    },
  },
  computed: {
    account() {
      return this.$store.state.entities.accounts.byId[this.accountRelation.account_uuid] || null;
    },
    paymentAccounts() {
      return this.account.paymentAccounts.map(
        (id) => this.$store.state.entities.accountPaymentAccounts.byId[id]
      );
    },
    paymentAccountNumbers() {
      return this.paymentAccounts.map((p) => p.attributes.number).filter((x) => x);
    },
    concatenatedPaymentAccountNumbers() {
      return this.paymentAccountNumbers.join(' | ');
    },
    offerLines() {
      return this.offer?.offerLines || [];
    },
    rootOfferLines() {
      return this.offerLines.filter((offerLine) =>
        ['parentOfferLineId', '_parentOfferLineId'].some((key) => offerLine[key] === null)
      );
    },
    childOfferLines() {
      return this.offerLines.filter((offerLine) =>
        ['parentOfferLineId', '_parentOfferLineId'].some((key) => offerLine[key] !== null)
      );
    },
    issueDate() {
      return moment(this.offer.issueDate).format('DD-MM-YYYY');
    },
    vatRate() {
      return this.accountRelation?.defaultVatRate || 0;
    },
    vatRateHumanReadable() {
      return Numbro(this.vatRate).format({
        output: 'percent',
        mantissa: 2,
      });
    },
    totalVatAmount() {
      return this.offerLines.reduce((total, offerLine) => total + offerLineVatAmount(offerLine), 0);
    },
    totalVatAmountHumanReadable() {
      return Numbro(this.totalVatAmount).format({
        thousandSeparated: true,
        mantissa: 2,
      });
    },
    totalNetAmount() {
      return this.offerLines.reduce((total, offerLine) => total + offerLineNetAmount(offerLine), 0);
    },
    totalNetAmountHumanReadable() {
      return Numbro(this.totalNetAmount).format({
        thousandSeparated: true,
        mantissa: 2,
      });
    },
    total() {
      return this.totalNetAmount + this.totalVatAmount;
    },
    totalHumanReadable() {
      return Numbro(this.total).format({
        thousandSeparated: true,
        mantissa: 2,
      });
    },
  },
};
</script>
